'use client';

import type { Configuration, Middleware } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { createContext, useMemo, useRef, type PropsWithChildren } from 'react';
import { createMpaInterceptHandler } from '@/components/dialogs/mpa/createMpaInterceptHandler';
import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { createHttpClient, refreshTokenIfNeeded, type HttpClient } from '@/data/dal/httpClient';
import { createMpaMiddleware } from '@/data/dal/mpaMiddleware';
import { createConfig } from './api-config';
import type { Environment } from './environment';
import { useEnvironment } from './useEnvironment';
import { useWorkspace } from './useWorkspace';
const createMiddleware = (authToken: string, updateAuthToken: (token: string) => void, environment: Environment, serverFlags: string | null): Middleware => {
  const useRefreshToken = !(environment.isDemo || environment.isDev || environment.isTest) || environment.enableForceLogoutOn401;
  let refreshPromise = Promise.resolve(authToken);
  let isRefreshing = false;
  const refreshToken = () => {
    if (!useRefreshToken || isRefreshing) {
      return refreshPromise;
    }
    isRefreshing = true;
    refreshPromise = refreshTokenIfNeeded(authToken, environment.clientApiBaseUrl, updateAuthToken).then(token => {
      isRefreshing = false;
      return token;
    }, () => {
      isRefreshing = false;
      return authToken;
    });
    return refreshPromise;
  };
  return {
    pre: async context => {
      if (serverFlags) {
        context.setHeaderParam('X-Server-Flags', serverFlags);
      }
      if (!context.getUrl().endsWith('/refresh')) {
        const newAuthToken = await refreshToken();
        context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
      }
      return context;
    },
    post: async context => {
      if (useRefreshToken && context.httpStatusCode === 401) {
        // The user is not authenticated, redirect to login
        location.href = '/logout';
      }
      return context;
    }
  };
};
export const NonProjectDalContext = createContext<{
  nonProjectDal: ReturnType<typeof nonProjectDalFactory>;
  httpClient: HttpClient;
  configuration: Configuration;
} | null>(null);
export const NonProjectDalProvider = ({
  children
}: PropsWithChildren) => {
  const env = useEnvironment();
  const searchParams = useSearchParams();
  const serverFlags = searchParams.get('serverFlags');
  const {
    sharedDialog: {
      open: openDialog,
      back: backDialog,
      replace: replaceDialog
    }
  } = useWorkspace();

  // Tracks whether our MPA dialog is currently up. Concurrent intercepts that
  // arrive while one is showing are cancelled. We use a local ref instead of
  // sharedDialog.isOpen so showMpaIntercept doesn't depend on the unstable
  // sharedDialog wrapper object.
  const mpaDialogOpenRef = useRef(false);
  const showMpaIntercept = useMemo(() => createMpaInterceptHandler({
    baseUrl: env.clientApiBaseUrl,
    authToken: env.authToken,
    openDialog,
    backDialog,
    replaceDialog,
    openRef: mpaDialogOpenRef
  }), [env.clientApiBaseUrl, env.authToken, openDialog, backDialog, replaceDialog]);
  const configuration = useMemo(() => {
    const config = createConfig(env.clientApiBaseUrl, env.authToken);
    config.middleware!.push(createMiddleware(env.authToken, env.updateAuthToken, env, serverFlags), createMpaMiddleware(showMpaIntercept));
    return config;
  }, [env, serverFlags, showMpaIntercept]);
  const httpClient = useMemo(() => createHttpClient(env.authToken, env.updateAuthToken, env), [env]);
  const nonProjectDal = useMemo(() => nonProjectDalFactory(httpClient, configuration, env.isDev || env.isTest), [configuration, httpClient, env]);
  return <NonProjectDalContext.Provider value={{
    nonProjectDal,
    httpClient,
    configuration
  }}>
      {children}
    </NonProjectDalContext.Provider>;
};